import { Button } from '@/components/_ui/Button'
import {
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/_ui/Dialog'
import { Separator } from '@/components/_ui/Separator'
import { Dialog } from '@radix-ui/react-dialog'
import type { ReactNode } from 'react'
import Spinner from '@/components/_ui/Spinner'
import { Alert, AlertDescription, AlertTitle } from '@/components/_ui/Alert'
import T from '@/components/Helpers/Translate'
import { useModals } from '../useModals'

// Prefix component name to make sure this doesn't get used anywhere else
// other than within the modals manager

export type ModalsManagerOpenConfirmModalProps = {
  title?: string
  isOpen?: boolean
  onConfirm?: () => void
  onCancel?: () => void
  description?: string | ReactNode
  confirmText?: string
  cancelText?: string
  variant?: 'destructive' | 'neutral'
  isLoading?: boolean
  error?: string
  allowClickOutside?: boolean
  modalId?: string
  zIndex?: number
  disabled?: boolean
}

export const ModalsManagerConfirmModal = (
  props: ModalsManagerOpenConfirmModalProps,
) => {
  const {
    title = 'are_you_sure',
    isOpen = true,
    confirmText = 'ok',
    cancelText = 'cancel',
    variant = 'neutral',
    isLoading = false,
    allowClickOutside = true,
    zIndex = 1000,
    modalId,
    description,
    onCancel,
    onConfirm,
    error,
    disabled = false,
  } = props

  const { modals } = useModals()

  const handleCloseModal = ({ open }: { open: boolean }) => {
    if (!open && modalId) {
      modals.updateModal(modalId, { isOpen: false })
      return
    }

    if (!open) {
      modals.closeModals()
    }
  }

  if (!isOpen) return null

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(value) => handleCloseModal({ open: value })}
    >
      <DialogContent
        style={{ zIndex }}
        onInteractOutside={(event) => {
          if (!allowClickOutside) event.preventDefault()
        }}
        className="max-w-sm"
      >
        <DialogHeader>
          <DialogTitle>
            <T>{title}</T>
          </DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        <Separator />
        {error && (
          <Alert variant="destructive">
            <AlertTitle>
              <T>error</T>
            </AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        <div className="flex justify-between">
          <DialogClose
            onClick={() => {
              if (onCancel) onCancel()
            }}
          >
            <Button variant="ghost">
              <T>{cancelText}</T>
            </Button>
          </DialogClose>
          <Button
            disabled={isLoading || disabled}
            variant={variant === 'destructive' ? 'destructive' : 'default'}
            onClick={() => {
              if (onConfirm) onConfirm()
            }}
          >
            {isLoading ? <Spinner /> : <T>{confirmText}</T>}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
